import { useEffect, useState } from "react";
import { useAuth } from "../../../../context/contextAuth";
import { useDesign } from "../../../../context/contextDesign";
import { useAwsIp } from "../../../../context/Dashboard/aws/contextIp";
import { useChat } from "../../../../context/contextChat";
import { useThemeMode } from "../../../../context/contextTheme";
import CustomModal from "../../../../components/modal";
import hexToRGBA from "../../../../utils/hexToRGBA";
import { downloadAwsEc2CSV } from "../../download";

const AwsIpTable = () => {
  const { bHeightVal, zoom } = useDesign();
  const { theme } = useThemeMode();
  const { user } = useAuth();
  const { networkResults, currentIndex, setCurrentIndex } = useAwsIp();
  const { setPrompt, setShowChat } = useChat();

  const [vulTable, setVulTable] = useState([]);
  const [modalContent, setModalContent] = useState("");
  const [showModal, setShowModal] = useState(false);

  /* ---------------- SEVERITY COLOR LOGIC ---------------- */
  const getSeverityStyle = (severity) => {
    const s = severity?.toLowerCase() || "";
    if (s.includes("high") || s.includes("critical"))
      return { bg: hexToRGBA("#dc3545", 0.1), color: "#dc3545", icon: "bi-exclamation-triangle-fill" };
    if (s.includes("medium"))
      return { bg: hexToRGBA("#ffc107", 0.1), color: "#856404", icon: "bi-exclamation-circle-fill" };
    if (s.includes("low"))
      return { bg: hexToRGBA("#198754", 0.1), color: "#198754", icon: "bi-info-circle-fill" };
    return { bg: hexToRGBA(theme.textMuted, 0.1), color: theme.textMuted, icon: "bi-question-circle" };
  };

  useEffect(() => {
    if (Array.isArray(networkResults) && networkResults.length > 0 && currentIndex !== null) {
      const scan = networkResults[currentIndex];
      setVulTable(scan?.vuln_data || []);
    }
  }, [currentIndex, networkResults]);

  const handleAIAssistance = (content) => {
    if (!content) return;
    const formattedPrompt = `Provide a technical breakdown for a ${user?.designation}. Detail this AWS EC2 network vulnerability and mitigation steps: \n\n"${content}"`.trim();
    setPrompt(formattedPrompt);
    setShowChat(true);
    setShowModal(false);
  };

  return (
    <div className="d-flex flex-column h-100 overflow-hidden" style={{ zoom }}>
      {(!networkResults || networkResults.length === 0) ? (
        <div className="flex-grow-1 d-flex flex-column justify-content-center align-items-center opacity-50">
          <i className="bi bi-cpu display-1 mb-3"></i>
          <h4 className="fw-bold">AWS EC2 IP Scan not yet started</h4>
        </div>
      ) : (
        <div className="d-flex flex-column h-100">

          {/* NATIVE HEADER & SELECTOR */}
          <div className="px-4 py-3 flex-shrink-0 border-bottom d-flex align-items-center justify-content-between" style={{ backgroundColor: theme.background }}>
            <div className="d-flex align-items-center gap-3">
              <span className="small fw-bold text-muted text-uppercase">Scan History:</span>
              <div className="d-flex bg-light p-1 rounded-3 overflow-auto no-scrollbar">
                {networkResults.map((scan, index) => (
                  <button
                    key={scan.scan_uuid || index}
                    className="border-0 px-4 py-1 rounded-2 fw-bold transition-all"
                    style={{
                      fontSize: "0.75rem",
                      whiteSpace: "nowrap",
                      backgroundColor: currentIndex === index ? "#344266" : "transparent",
                      color: currentIndex === index ? "white" : theme.textMuted,
                      boxShadow: currentIndex === index ? "0 2px 6px rgba(0,0,0,0.15)" : "none",
                    }}
                    onClick={() => setCurrentIndex(index)}
                  >
                    Scan {index + 1}
                  </button>
                ))}
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary d-flex align-items-center gap-2 fw-bold"
                onClick={() => downloadAwsEc2CSV(networkResults, 0)}
              >
                <i className="bi bi-download"></i> Download CSV
              </button>
              {/* <div className="badge bg-primary-subtle text-primary border border-primary-subtle px-3 py-2">
                Total Vulnerabilities: {networkResults.length}
              </div> */}
            </div>
          </div>

          {/* TABLE AREA */}
          <div className="flex-grow-1 px-4 py-3 overflow-hidden">
            <div className="card border-0 rounded-4 shadow-sm h-100 overflow-hidden" style={{ backgroundColor: theme.background }}>
              <div className="table-responsive h-100 custom-scrollbar">
                <table className="table align-middle mb-0" style={{ color: theme.textColor }}>
                  <thead style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#f8f9fa" }}>
                    <tr className="small text-muted text-uppercase fw-bolder" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>
                      <th className="ps-4 border-0 py-3" style={{ width: '30%' }}>Vulnerability Title</th>
                      <th className="text-center border-0 py-3">Severity</th>
                      <th className="text-center border-0 py-3">Environment</th>
                      {/* <th className="text-center border-0 py-3">Port</th> */}
                      <th className="text-center border-0 py-3">Risk Assessment</th>
                      <th className="pe-4 border-0 py-3 text-end">Analysis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vulTable.map((vuln, index) => {
                      const severity = getSeverityStyle(vuln.baseSeverity);
                      return (
                        <tr key={index} className="native-row-hover" style={{ borderBottom: `1px solid ${hexToRGBA(theme.textColor, 0.05)}` }}>
                          {/* Title & CVE */}
                          <td className="ps-4 py-3">
                            <div className="fw-bold text-wrap" style={{ fontSize: "0.85rem", maxWidth: "280px" }}>{vuln.title}</div>
                            <div className="text-muted mt-1" style={{ fontSize: "0.65rem" }}>
                              <span className="badge bg-light text-dark border me-1">CVE</span>
                              {vuln.codes || 'N/A'}
                            </div>
                          </td>

                          {/* Severity Badge */}
                          <td className="text-center">
                            <div className="d-inline-flex align-items-center gap-1 px-3 py-1 rounded-pill fw-bold" style={{ backgroundColor: severity.bg, color: severity.color, fontSize: '0.65rem' }}>
                              <i className={`bi ${severity.icon}`}></i>
                              {vuln.baseSeverity}
                            </div>
                          </td>

                          {/* Platform/Type */}
                          <td className="text-center">
                            <div className="fw-medium small">{vuln.type}</div>
                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>{vuln.platform}</div>
                          </td>

                          {/* Port */}
                          {/* <td className="text-center">
                            <span className="badge bg-secondary-subtle text-secondary fw-bold">{vuln.port || '80/443'}</span>
                          </td> */}

                          {/* Risk Metrics */}
                          <td className="text-center small">
                            <div className="d-flex flex-column align-items-center">
                              <span className="text-muted">Impact: <b className="text-dark">{vuln.impact}</b></span>
                              <span className="text-muted" style={{ fontSize: '10px' }}>Exploit: {vuln.exploitability}</span>
                            </div>
                          </td>

                          {/* AI Action */}
                          <td className="pe-4 py-3 text-end">
                            <button
                              className="btn btn-sm btn-outline-primary rounded-pill px-3 py-1 fw-bold border-2"
                              style={{ fontSize: '0.7rem' }}
                              onClick={() => { setModalContent(vuln.title); setShowModal(true); }}
                            >
                              <i className="bi bi-stars me-1"></i> AI Fix
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL */}
      <CustomModal
        show={showModal}
        onClose={() => setShowModal(false)}
        title="AWS Security Insight"
        footer={
          <div className="d-flex gap-2">
            <button className="btn btn-light fw-bold" onClick={() => setShowModal(false)}>Close</button>
            <button className="btn btn-primary px-4 fw-bold shadow-sm" onClick={() => handleAIAssistance(modalContent)}>
              <i className="bi bi-stars me-2"></i>Generate Mitigation
            </button>
          </div>
        }
      >
        <div className="p-2" style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: "1.6", color: "#444" }}>
          <h6 className="fw-bold mb-3 text-light">Selected Vulnerability:</h6>
          <div className="bg-light p-3 rounded-3 border">{modalContent}</div>
          <p className="mt-3 small text-light">
            The AI engine will analyze this EC2 instance vulnerability and provide specific AWS-compliant CLI commands or Console steps for remediation, based on your role as {user?.designation}.
          </p>
        </div>
      </CustomModal>

      <style>{`
        .native-row-hover:hover {
          background-color: ${hexToRGBA("#344266", 0.02)};
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { 
          background: rgba(52, 66, 102, 0.1); 
          border-radius: 10px; 
        }
      `}</style>
    </div>
  );
};

export default AwsIpTable;